import React, { useState } from 'react';
import { useAuth } from '../../auth/AuthContext';

export const LoginScreen: React.FC = () => {
  const { login, loading, error } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  // local mode: login | forgot | sent
  const [mode, setMode] = useState<'login' | 'forgot' | 'sent'>('login');
  const [resetId, setResetId] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');

  function onSubmit(e: React.FormEvent){
    e.preventDefault();
    login(username, password);
  }

  // Forgot password submit
  async function requestReset(e: React.FormEvent) {
    e.preventDefault();
    setResetError('');
    setResetLoading(true);
    try {
      const res = await fetch('/api/auth/password/reset/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: resetId.trim() })
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || 'Reset request failed');
      }
      setMode('sent');
    } catch (e: any) {
      // Do not leak existence; still show sent state
      console.warn('[ForgotPassword] error:', e?.message);
      setMode('sent');
    } finally {
      setResetLoading(false);
    }
  }

  return (
    <main className="login-screen" role="main" aria-labelledby="loginTitle">
      <section className="login-shell">
        <div className="login-brand-panel">
          <img src="/avcs-logo.png" alt="AVCS Logo" className="login-logo" />
          <div className="login-brand-copy">
            <span className="login-kicker">Integrated Supply Chain Program Platform</span>
            <h1 className="login-brand-title">Operational visibility for every livestock journey.</h1>
            <p>Secure access to deal dashboards, animal movement, documentation, and supply chain program records.</p>
          </div>
          <div className="login-brand-stats" aria-label="Platform highlights">
            <div>
              <strong>Live</strong>
              <span>tracking</span>
            </div>
            <div>
              <strong>Admin</strong>
              <span>workflows</span>
            </div>
            <div>
              <strong>GS1</strong>
              <span>ready</span>
            </div>
          </div>
        </div>

        <div className="login-form-panel">
          <div className="login-form-header">
            {mode === 'login' && (
              <>
                <span className="login-eyebrow">Welcome back</span>
                <h2 id="loginTitle">Sign in to AVCS</h2>
                <p>Use your AVCS account to continue.</p>
              </>
            )}
            {mode === 'forgot' && (
              <>
                <span className="login-eyebrow">Account recovery</span>
                <h2 id="loginTitle">Reset your password</h2>
                <p>Enter your email or username and we will send a reset link if the account exists.</p>
              </>
            )}
            {mode === 'sent' && (
              <>
                <span className="login-eyebrow">Email sent</span>
                <h2 id="loginTitle">Check your inbox</h2>
                <p>If an account exists for that identifier, a reset link will arrive shortly.</p>
              </>
            )}
          </div>

          {mode === 'login' && (
            <div aria-live="polite">
              {error ? <div className="alert error" role="alert">{error}</div> : null}
            </div>
          )}

          {mode === 'login' && (
            <form onSubmit={onSubmit} className="login-form" aria-label="Sign in form">
              <label>
                <span>Username or email</span>
                <input
                  type="text"
                  value={username}
                  onChange={(e)=>setUsername(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoComplete="username"
                  autoFocus
                  minLength={3}
                />
              </label>
              <label>
                <span>Password</span>
                <div className="login-password-field">
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={password}
                    onChange={(e)=>setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    autoComplete="current-password"
                    minLength={8}
                  />
                  <button type="button" onClick={()=>setShowPw(v=>!v)} aria-pressed={showPw} aria-label={showPw ? 'Hide password' : 'Show password'}>
                    {showPw ? 'Hide' : 'Show'}
                  </button>
                </div>
              </label>
              <div className="login-form-actions">
                <button className="login-primary-btn" type="submit" disabled={loading || !username || !password}>
                  {loading ? (
                    <span className="login-loading">
                      <span className="spinner" aria-hidden="true" /> Signing in...
                    </span>
                  ) : 'Sign in'}
                </button>
                <button className="login-secondary-btn" type="reset" onClick={()=>{ setUsername(''); setPassword(''); }}>Clear</button>
              </div>
              <button
                type="button"
                className="login-link-btn"
                onClick={()=> setMode('forgot')}
              >Forgot password?</button>
            </form>
          )}

          {mode === 'forgot' && (
            <form onSubmit={requestReset} className="login-form" aria-label="Forgot password form">
              <label>
                <span>Email or username</span>
                <input
                  type="text"
                  value={resetId}
                  onChange={(e)=>setResetId(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoFocus
                  minLength={3}
                />
              </label>
              {resetError ? <div className="alert error" role="alert">{resetError}</div> : null}
              <div className="login-form-actions">
                <button className="login-primary-btn" type="submit" disabled={resetLoading || !resetId}>
                  {resetLoading ? 'Sending...' : 'Send reset link'}
                </button>
                <button type="button" className="login-secondary-btn" onClick={()=>{ setMode('login'); setResetId(''); }}>Back</button>
              </div>
            </form>
          )}

          {mode === 'sent' && (
            <div className="login-success-state">
              <div className="alert success" role="status">
                If an account exists, a password reset email is on its way.
              </div>
              <div className="login-form-actions">
                <button className="login-primary-btn" onClick={()=>{ setMode('login'); }}>Return to sign in</button>
                <button className="login-secondary-btn" onClick={()=>{ setMode('forgot'); }}>Send another</button>
              </div>
            </div>
          )}
        </div>
      </section>
    </main>
  );
};
